import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

interface HeroSectionProps {
  onExploreClick: () => void;
}

export default function HeroSection({ onExploreClick }: HeroSectionProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("https://readdy.ai/api/search-image?query=Seamless%20artistic%20pattern%20of%20diverse%20fashion%20items%20and%20lifestyle%20products%20floating%20with%20generous%20spacing%20on%20soft%20warm%20cream%20background%2C%20minimalist%20product%20cutout%20collage%20with%20sneakers%20handbags%20clothing%20accessories%20and%20small%20decor%20objects%20scattered%20organically%2C%20warm%20neutral%20tones%20with%20subtle%20terracotta%20and%20sage%20accents%2C%20clean%20contemporary%20aesthetic%2C%20soft%20diffused%20studio%20lighting%2C%20editorial%20flat%20lay%20style%20with%20airy%20composition&width=1600&height=1200&seq=hero-bg-v3&orientation=landscape")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-900/70 via-primary-900/40 to-primary-900/80"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-20 pb-10">
        <div className="inline-block px-5 py-1.5 border border-background-50/30 rounded-full mb-6">
          <span className="text-xs md:text-sm text-background-50/80 tracking-wide font-medium">Kakobuy Spreadsheet 2026</span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading text-background-50 leading-tight mb-5">
          Kakobuy Spreadsheet:
          <br />
          <span className="text-accent-300">Best Finds</span> &amp; Shopping Guides
        </h1>

        <p className="text-base md:text-lg text-background-50/70 max-w-2xl mx-auto mb-8 leading-relaxed">
          Access carefully organized Kakobuy Spreadsheet lists featuring trending fashion,
          sneakers, accessories and hidden gems. Compare products, discover better finds,
          and shop smarter.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-3 bg-background-50 text-foreground-800 text-sm font-medium px-6 py-3.5 rounded-full hover:bg-background-100 transition-colors min-w-[220px] justify-between cursor-pointer"
            >
              <span>Browse Categories</span>
              <i className={`text-base transition-transform ${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-2xl shadow-lg border border-background-200/50 py-2 w-64 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors text-left"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-primary-500 text-background-50 text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-primary-400 transition-all transform hover:scale-105 whitespace-nowrap min-w-[180px]"
          >
            Kakobuy Sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="border-2 border-background-50 text-background-50 text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-background-50 hover:text-primary-700 transition-all transform hover:scale-105 whitespace-nowrap min-w-[180px]"
          >
            Buy It Now
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-5 mt-10">
          {['Updated Product Lists', 'Detailed Comparisons', 'Shopping Research Guides', 'Beginner Friendly'].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-background-50/60">
              <i className="ri-check-line text-accent-400"></i>
              {badge}
            </span>
          ))}
        </div>

        <button
          onClick={onExploreClick}
          className="mt-14 mx-auto flex flex-col items-center gap-2 text-background-50/40 hover:text-background-50/80 transition-colors cursor-pointer animate-bounce"
        >
          <span className="text-xs tracking-widest uppercase">Explore</span>
          <i className="ri-arrow-down-line text-lg"></i>
        </button>
      </div>
    </section>
  );
}